// Shows current usage and remaining evaluations for the month.
// Appears at the top of the drop zone when user is close to their limit.
// Disappears entirely for Lab tier users (unlimited).

import React from 'react';
import type { TierKey } from '../types';
import { TIER_LABELS } from '../utils/formatters';
import shared from '../styles/shared.module.css';

interface UsageLimitBannerProps {
  used: number;
  limit: number;
  tier: TierKey;
  onUpgradeClick: () => void;
}

export default function UsageLimitBanner({
  used,
  limit,
  tier,
  onUpgradeClick,
}: UsageLimitBannerProps) {
  // Lab tier is unlimited — no banner needed
  if (limit === Infinity) return null;

  const remaining = Math.max(0, limit - used);
  const pct = Math.min(100, (used / limit) * 100);
  const isLow = remaining <= 2;
  const isExhausted = remaining === 0;

  return (
    <div className={shared.card} style={{
      width: '100%',
      maxWidth: 580,
      marginBottom: 16,
      borderColor: isExhausted
        ? 'var(--verdict-skip-border)'
        : isLow
        ? 'var(--verdict-skim-border)'
        : 'var(--bg-card-border)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      }}>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}>
          {TIER_LABELS[tier]} — {isExhausted ? 'No evaluations remaining' : `${remaining} of ${limit} remaining this month`}
        </span>
        {tier !== 'lab' && (
          <button
            onClick={onUpgradeClick}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 700,
              background: 'var(--accent)',
              color: 'var(--bg-primary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              padding: '4px 10px',
              cursor: 'pointer',
            }}
          >
            Upgrade
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div style={{
        height: 4,
        background: 'var(--score-track)',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: isExhausted
            ? 'var(--score-low)'
            : isLow
            ? 'var(--score-mid)'
            : 'var(--score-high)',
          borderRadius: 2,
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}